import {
  mutationOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { useDispatch } from 'react-redux';

import { fertWorkApi } from '@/app/services/fertWork';
import type { AccessToken } from '@/lib/tokenStorage';
import { useAccessToken } from '@/services/auth';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

export type MoveSireGroupRequest = {
  sessionId: string;
  sireId: string;
  semenType?: string;
  collectionCode?: string;
  sireNote?: string;
  sourceRunId: string;
  targetRunId: string;
};

export const useMoveSireGroupBetweenRunsMutation = () => {
  const queryClient = useQueryClient();
  const token = useAccessToken();

  return useMutation({
    mutationFn: async ({
      sessionId,
      sireId,
      semenType,
      collectionCode,
      sireNote,
      sourceRunId,
      targetRunId,
    }: MoveSireGroupRequest): Promise<void> => {
      const response = await fetch(
        `${API_BASE_URL}/api/fertwork/session/${sessionId}/runs/move-sire-group`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
            'X-Tunnel-Authorization': `tunnel ${import.meta.env.VITE_DEV_TUNNEL_TOKEN}`,
          },
          credentials: 'include',
          body: JSON.stringify({
            sireId,
            semenType,
            collectionCode,
            sireNote,
            sourceRunId,
            targetRunId,
          }),
        },
      );

      if (!response.ok) {
        const error = await response.text();
        throw new Error(error || `HTTP error ${response.status}`);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fertWorkRuns'] });
    },
  });
};

export type FertWorkSyncField =
  | 'OPU_SITE'
  | 'MAT_TIME'
  | 'TOTAL_OOCYTES'
  | 'DONOR_LOCATION'
  | 'DONOR'
  | 'DONOR_BREED'
  | 'SIRE'
  | 'SEMEN_PULL_SIRE_NOTE'
  | 'SIRE_BREED'
  | 'COLLECTION_CODE'
  | 'SEMEN_TYPE'
  | 'SEMEN_COMMENTS'
  | 'SEMEN_OWNER'
  | 'ACCOUNT'
  | 'SHIP_LABEL'
  | 'EXPORT'
  | 'SPLIT'
  | 'SPLIT_OOCYTES';

export type FertWorkSyncFieldChange = {
  field: FertWorkSyncField;
  oldValue?: string | null;
  newValue?: string | null;
};

export type FertWorkSyncRecordUpdate = {
  detailId: string;
  donorName?: string | null;
  sireName?: string | null;
  changes: FertWorkSyncFieldChange[];
};

export type FertWorkSyncNewRecord = {
  sourceRecordId: string;
  donorName?: string | null;
  sireName?: string | null;
  semenType?: string | null;
  collectionCode?: string | null;
  totalOocytes: number;
};

export type FertWorkSyncSireValues = {
  sireName?: string | null;
  semenType?: string | null;
  collectionCode?: string | null;
};

export type FertWorkSyncSplitHalf = {
  detailId: string;
  donorName?: string | null;
  isOriginal: boolean;
  current: FertWorkSyncSireValues;
};

export type FertWorkSyncSplitSireDecision = {
  decisionId: string;
  donorName?: string | null;
  source: FertWorkSyncSireValues;
  halves: FertWorkSyncSplitHalf[];
  matchingHalfDetailId?: string | null;
};

export type FertWorkSyncMissingReason =
  | 'DELETED'
  | 'CANCELLED'
  | 'DATE_CHANGED'
  | 'SITE_CHANGED'
  | 'NO_LONGER_ELIGIBLE';

export type FertWorkSyncMissingScheduleDecision = {
  decisionId: string;
  reason: FertWorkSyncMissingReason;
  isOnRuns: boolean;
  isOnLabels: boolean;
  rows: {
    detailId: string;
    donorName?: string | null;
    sireName?: string | null;
    totalOocytes: number;
  }[];
};

export type FertWorkSyncPreview = {
  previewToken: string;
  hasChanges: boolean;
  newRecords: FertWorkSyncNewRecord[];
  updates: FertWorkSyncRecordUpdate[];
  splitSireDecisions: FertWorkSyncSplitSireDecision[];
  missingScheduleDecisions: FertWorkSyncMissingScheduleDecision[];
  unlinkedRecordCount: number;
};

// REMOVE removes the rows, KEEP asks again next time, ORPHAN stops syncing them for good.
export type FertWorkSyncMissingAction = 'REMOVE' | 'KEEP' | 'ORPHAN';

export type ApplyFertWorkSyncInput = {
  previewToken: string;
  splitSireChoices: { decisionId: string; targetDetailId: string | null }[];
  missingScheduleChoices: {
    decisionId: string;
    action: FertWorkSyncMissingAction;
  }[];
  skippedUpdateDetailIds: string[];
  skippedNewRecordIds: string[];
};

export type FertWorkSyncApplyResult = {
  addedCount: number;
  updatedCount: number;
  removedCount: number;
  newRecordIds: string[];
  totalRecordCount: number;
};

export const FERT_WORK_PREVIEW_OUTDATED_CODE = 'PreviewOutdated';

export class FertWorkRequestError extends Error {
  readonly status: number;
  readonly apiMessage?: string;
  readonly code?: string;

  constructor(status: number, apiMessage?: string, code?: string) {
    super(apiMessage ?? `HTTP error ${status}`);
    this.name = 'FertWorkRequestError';
    this.status = status;
    this.apiMessage = apiMessage;
    this.code = code;
  }
}

function headers(token: AccessToken): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
    'X-Tunnel-Authorization': `tunnel ${import.meta.env.VITE_DEV_TUNNEL_TOKEN}`,
  };
}

async function toFertWorkRequestError(
  response: Response,
): Promise<FertWorkRequestError> {
  const text = await response.text();
  try {
    const { message, code } = JSON.parse(text) as {
      message?: unknown;
      code?: unknown;
    };
    return new FertWorkRequestError(
      response.status,
      typeof message === 'string' && message ? message : undefined,
      typeof code === 'string' && code ? code : undefined,
    );
  } catch {
    return new FertWorkRequestError(response.status);
  }
}

export function previewFertWorkSyncMutationOptions(token: AccessToken) {
  return mutationOptions({
    mutationKey: ['fert-work', 'preview-sync'],
    async mutationFn(sessionId: string): Promise<FertWorkSyncPreview> {
      const response = await fetch(
        `${API_BASE_URL}/api/v2/fertwork/session/${sessionId}/sync/preview`,
        {
          method: 'POST',
          headers: headers(token),
          credentials: 'include',
        },
      );
      if (!response.ok) {
        throw await toFertWorkRequestError(response);
      }
      return response.json();
    },
  });
}

export function applyFertWorkSyncMutationOptions(token: AccessToken) {
  return mutationOptions({
    mutationKey: ['fert-work', 'apply-sync'],
    async mutationFn({
      sessionId,
      input,
    }: {
      sessionId: string;
      input: ApplyFertWorkSyncInput;
    }): Promise<FertWorkSyncApplyResult> {
      const response = await fetch(
        `${API_BASE_URL}/api/v2/fertwork/session/${sessionId}/sync`,
        {
          method: 'POST',
          headers: headers(token),
          credentials: 'include',
          body: JSON.stringify(input),
        },
      );
      if (!response.ok) {
        throw await toFertWorkRequestError(response);
      }
      return response.json();
    },
  });
}

export function usePreviewFertWorkSyncMutation() {
  const token = useAccessToken();
  return useMutation(previewFertWorkSyncMutationOptions(token));
}

export function useApplyFertWorkSyncMutation() {
  const token = useAccessToken();
  const dispatch = useDispatch();
  return useMutation({
    ...applyFertWorkSyncMutationOptions(token),
    onSuccess() {
      dispatch(
        fertWorkApi.util.invalidateTags([
          'FertWorkSessionDetail',
          'FertWorkRuns',
          'FertWorkUnassignedDonors',
        ]),
      );
    },
  });
}
